import { clock, m, onLangChange } from '../i18n';
import { sfx } from '../audio/sfx';
import { stopSpeech } from '../audio/speech';
import { bus } from '../core/bus';
import { hasGame, S } from '../core/game';
import { dateLabel } from '../content/calendar';
import { h, $, type Child } from '../ui/dom';
import { layers } from '../ui/layers';

export type Tab = 'chats' | 'calls' | 'feed' | 'radio';

export interface PhoneView {
  title: string;
  body: Child;
  /** Optional bottom bar (chat input). */
  bar?: Node | null;
  /** Back button target; omitted = back to the home screen. */
  back?: () => void;
}

/** An icon on the phone's home screen. Tabs open inside the phone; tools close it and open their own panel. */
export interface PhoneApp {
  id: string;
  icon: string;
  label: () => string;
  /** Gradient colours of the icon tile. */
  colors: [string, string];
  run: () => void;
  badge?: () => number;
  /** Shown in the bottom dock of the home screen. */
  dock?: boolean;
}

type Renderer = () => PhoneView;

const renderers = new Map<Tab, Renderer>();
const apps: PhoneApp[] = [];
let tab: Tab | 'home' = 'home';
let override: Renderer | null = null;
let unread = 2;

export function registerTab(t: Tab, r: Renderer): void {
  renderers.set(t, r);
}

export function registerApps(list: PhoneApp[]): void {
  apps.push(...list);
}

export function openPhone(t: Tab | 'home' = 'home'): void {
  tab = t;
  override = null;
  if (t === 'chats' || t === 'home') unread = 0;
  layers.open('phone');
  $('phone').classList.add('show');
  render();
  bus.emit('change');
}

export function closePhone(): void {
  if (!layers.isOpen('phone')) return;
  $('phone').classList.remove('show');
  layers.close('phone');
  stopSpeech();
  override = null;
}

export function phoneOpen(): boolean {
  return layers.isOpen('phone');
}

export function unreadCount(): number {
  return unread;
}

/** A new message arrived while the phone was closed: raise the badge on the phone button. */
export function bumpUnread(n = 1): void {
  if (phoneOpen() && tab === 'chats') return render();
  unread += n;
  if (phoneOpen()) render();
  bus.emit('change');
}

/** Replace the current tab content with a sub-view (a chat room, a call...). */
export function pushView(r: Renderer): void {
  override = r;
  render();
}

export function popView(): void {
  override = null;
  render();
}

function goHome(): void {
  tab = 'home';
  override = null;
  render();
}

function appIcon(a: PhoneApp): HTMLElement {
  const n = a.badge?.() ?? 0;
  return h(
    'button',
    {
      class: 'ph-app',
      onclick: () => {
        sfx('click');
        a.run();
      },
    },
    h('span', { class: 'ico', style: `--a:${a.colors[0]};--b:${a.colors[1]}` }, a.icon, n ? h('span', { class: 'badge' }, String(n)) : null),
    h('small', null, a.label()),
  );
}

function home(): HTMLElement {
  const s = hasGame() ? S() : null;
  return h(
    'div',
    { class: 'ph-home' },
    h('div', { class: 'ph-clock' }, h('b', null, s ? clock(s.time) : '08:00'), h('small', null, s ? dateLabel() : '')),
    h('div', { class: 'ph-apps' }, apps.filter((a) => !a.dock).map(appIcon)),
    h('div', { class: 'ph-dockbar' }, apps.filter((a) => a.dock).map(appIcon)),
  );
}

export function render(): void {
  if (!phoneOpen()) return;
  const screen = $('phoneScreen');
  if (tab === 'home' && !override) {
    screen.replaceChildren(
      h('div', { class: 'ph-island' }),
      h('div', { class: 'ph-nav' }, h('span', { class: 'ph-title' }, ''), h('button', { class: 'ph-close', 'aria-label': m().common.close, onclick: closePhone }, '✕')),
      home(),
    );
    return;
  }
  const view = (override ?? renderers.get(tab as Tab)!)();
  // Remember which text field is being typed in, so re-rendering never loses it.
  const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
  const keep = active?.dataset?.key ? { key: active.dataset.key, start: active.selectionStart, end: active.selectionEnd } : null;
  const oldBody = screen.querySelector('.ph-body');
  const scroll = oldBody?.scrollTop ?? 0;
  const body = h('div', { class: 'ph-body' }, view.body);
  screen.replaceChildren(
    h('div', { class: 'ph-island' }),
    h(
      'div',
      { class: 'ph-nav' },
      h('button', { class: 'ph-back', 'aria-label': m().common.back, onclick: () => (sfx('click'), view.back ? view.back() : goHome()) }, '‹'),
      h('span', { class: 'ph-title' }, view.title),
      h('button', { class: 'ph-close', 'aria-label': m().common.close, onclick: closePhone }, '✕'),
    ),
    body,
    view.bar ?? '',
    h('button', { class: 'ph-home-btn', 'aria-label': 'home', onclick: () => (sfx('click'), goHome()) }, h('i')),
  );
  const scroller = body.querySelector('.room-msgs');
  if (scroller) scroller.scrollTop = scroller.scrollHeight;
  else body.scrollTop = scroll;
  if (keep) {
    const el = body.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-key="${keep.key}"]`);
    if (el) {
      el.focus({ preventScroll: true });
      try {
        el.setSelectionRange(keep.start, keep.end);
      } catch {
        /* not all inputs support selection */
      }
    }
  }
}

export function initPhone(): void {
  $('phone').addEventListener('pointerdown', (e) => {
    if (e.target === $('phone')) closePhone();
  });
  onLangChange(render);
}
